import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  DefaultOptions,
  InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { FC, PropsWithChildren, useMemo } from 'react';

import { APIConfiguration } from '@savgroup-front-common/configuration';
import { logVerbose } from '@savgroup-front-common/configuration/appInsights/AppInsights';

import { selectLocale } from '../../domains/i18n/selectors';
import useCommonTypedSelector from '../../domains/useCommonTypedSelector';
import {
  buildSessionRequestHeaders,
  fetchWithSessionReport,
  getIsSessionCookieModeEnabled,
  toSameOriginApiUrl,
} from '../../services/sessionCookieMode';
import { userManager } from '../../services/userManager';

import { FETCH_POLICIES } from './fetchPolicies';

const getGraphqlEndpoint = (): string =>
  getIsSessionCookieModeEnabled()
    ? toSameOriginApiUrl(APIConfiguration.graphQlGateway)
    : APIConfiguration.graphQlGateway;

const httpLink = createHttpLink({
  uri: ({ operationName }) =>
    `${getGraphqlEndpoint()}?operationName=${operationName}`,
  fetch: fetchWithSessionReport,
});

const DEFAULT_OPTIONS: DefaultOptions = {
  watchQuery: {
    fetchPolicy: FETCH_POLICIES.CACHE_AND_NETWORK,
    nextFetchPolicy: FETCH_POLICIES.CACHE_FIRST,
    errorPolicy: 'all',
  },
  query: {
    fetchPolicy: FETCH_POLICIES.CACHE_FIRST,
    errorPolicy: 'all',
  },
  mutate: {
    errorPolicy: 'all',
  },
};

interface GraphqlProviderProps extends PropsWithChildren {
  defaultOptions?: DefaultOptions;
}

export const GraphqlProvider: FC<GraphqlProviderProps> = ({
  children,
  defaultOptions = DEFAULT_OPTIONS,
}) => {
  const locale = useCommonTypedSelector(selectLocale);

  const client = useMemo(() => {
    const authLink = setContext(async () => {
      if (getIsSessionCookieModeEnabled()) {
        return {
          headers: {
            'Accept-Language': locale,
            ...buildSessionRequestHeaders(getGraphqlEndpoint()),
          },
        };
      }

      const user = await userManager.getUser();

      let accessToken = null;

      if (user && !user.expired && user.access_token.length > 43) {
        accessToken = user.access_token;
      } else if (user && (user.expired || user.access_token.length <= 43)) {
        await userManager.signinSilent({
          resource: APIConfiguration.apiBase,
        });

        accessToken = (await userManager.getUser())?.access_token;
      } else {
        logVerbose('User is not logged in');

        sessionStorage.clear();
      }

      return {
        headers: {
          'Accept-Language': locale,
          authorization: accessToken ? `Bearer ${accessToken}` : '',
        },
      };
    });

    return new ApolloClient({
      link: authLink.concat(httpLink),
      cache: new InMemoryCache(),
      defaultOptions,
    });
  }, [locale, defaultOptions]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
